import { ModalContextProps, useModalContext } from '@adsk/napi-shared'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../../store'
import { Flex } from '@adsk/alloy-react-box'
import Button, { LinkButton } from '@adsk/alloy-react-button'
import React, { useEffect } from 'react'
import _ from 'lodash'
import {
	setUserInfoChanged,
	showSubmitButtonLoader,
} from '../../../store/actions'
import { BugsnagNotify } from '@adsk/napi-bugsnag'
import { getPackageInfo } from '../../../utils'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../../types/AnalyticsTypes'
import {
	NapiClient,
	OnboardedBuilderNetworkResponse,
	UpdateBNUserDto,
} from '@adsk/napi-api-client'
import { checkHasUserInfoChanged, useAsyncError } from '../../../utils/helpers'
import { useTranslation } from '../../../../i18n'
import { useUser, EntitlementsUser } from '@adsk/alloy-provider'

const FooterComponent = (): JSX.Element | null => {
	const { version, name } = getPackageInfo()
	const { closeModal } = useModalContext() as ModalContextProps
	const {
		state: {
			marketingCompanyType,
			submitState,
			displaySubmitButtonLoader,
			userInfo,
			profileReadOnlyMode,
			flowType,
			userInfoChanged,
			immutableUserInfo,
			selectionsList,
		},
		dispatch,
		externalProps,
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const throwError = useAsyncError()
	const { t } = useTranslation()
	const { setUser, user } = useUser()

	const hasUserInfoChanged = () => {
		if (userInfo && immutableUserInfo) {
			dispatch(
				setUserInfoChanged(
					checkHasUserInfoChanged(
						userInfo,
						immutableUserInfo,
						marketingCompanyType,
						selectionsList
					)
				)
			)
		}
	}
	useEffect(() => {
		hasUserInfoChanged()
	}, [userInfo])

	const updateAccUser = (response: OnboardedBuilderNetworkResponse) => {
		try {
			if (setUser) {
				setUser({
					...user,
					name: `${response?.firstName?.trim()} ${response?.lastName?.trim()}`,
					firstName: response?.firstName?.trim(),
					lastName: response?.lastName?.trim(),
					imageUrl: response?.avatarUrl,
				} as unknown as EntitlementsUser)
			}
		} catch (e) {
			BugsnagNotify(e as Error, {
				message:
					'[Edit Profile Action] Error updating user information in the acc via setUser hook',
			})
		}
	}

	const saveHandler = () => {
		dispatch(showSubmitButtonLoader(true))
		const companyName = _.get(userInfo, 'companyName') as unknown as string
		const selectedMarketingCompanyType = Object.keys(
			marketingCompanyType
		).filter((value) => marketingCompanyType[value]) as string[]
		const jobTitle = userInfo?.title?.trim()
		NapiClient.updateUserProfile({
			..._.omit(userInfo, ['updatedAvatarUrl']),
			firstName: userInfo?.firstName?.trim(),
			lastName: userInfo?.lastName?.trim(),
			avatarUrl: userInfo?.updatedAvatarUrl,
			companyName: companyName?.trim(),
			companyType: selectedMarketingCompanyType,
			title: jobTitle,
		} as unknown as UpdateBNUserDto)
			.then((response: OnboardedBuilderNetworkResponse) => {
				externalProps?.setShowModal?.(false)
				track(ANALYTICS_TYPES.NAPI_USERS_MODIFIED_PROFILE, {
					package: name,
					version,
					flowType,
				})
				updateAccUser(response)
				closeModal()
			})
			.catch((error: Error) => {
				BugsnagNotify(error as Error, {
					message:
						'Network Update User Profile Network API Error on ProfileEdit component',
				})
				track(ANALYTICS_TYPES.NAPI_USER_PROFILE_MODIFICATION_FAILED, {
					package: name,
					version,
				})
				throwError(error)
			})
			.finally(() => {
				dispatch(showSubmitButtonLoader(false))
			})
	}

	return !profileReadOnlyMode ? (
		<Flex
			justifyContent={'flex-end'}
			alignItems={'space-between'}
			width={'100%'}
		>
			<LinkButton
				style={{ order: 1, paddingRight: '10px' }}
				onClick={() => {
					externalProps?.setShowModal?.(false)
					track(ANALYTICS_TYPES.NAPI_USER_PROFILE_MODIFICATION_CANCELLED, {
						package: name,
						version,
					})
					closeModal()
				}}
				size={LinkButton.SIZES.SMALL}
			>
				{t('CANCEL')}
			</LinkButton>
			<Button
				data-testid={'saveButtonTestId'}
				disabled={!userInfoChanged || !submitState}
				style={{ order: 2 }}
				variant={Button.VARIANTS.PRIMARY}
				onClick={saveHandler}
				loading={displaySubmitButtonLoader}
			>
				{t('SAVE')}
			</Button>
		</Flex>
	) : null
}

export default FooterComponent
